import { format, isAfter } from 'date-fns';
import api from '../config/api';
import { parseFlightNumberInput } from './flightJourneyUtils';
import { resolveAirlineIata } from './airlineIata';

/**
 * Live / schedule flight status is only useful for active or upcoming trips.
 * Matches Trips.js getTripStatus: completed once now is after trip.end_date.
 */
export const isTripEligibleForFlightStatus = (trip) => {
  if (!trip?.end_date) return false;
  return !isAfter(new Date(), new Date(trip.end_date));
};

export const LIVE_STATUS_LABEL_KEYS = {
  scheduled: 'flightAlerts.status.scheduled',
  active: 'flightAlerts.status.active',
  in_air: 'flightAlerts.status.inAir',
  landed: 'flightAlerts.status.landed',
  cancelled: 'flightAlerts.status.cancelled',
  delayed: 'flightAlerts.status.delayed',
  diverted: 'flightAlerts.status.diverted',
  incident: 'flightAlerts.status.incident',
};

/** MUI-ish color tokens used by trip chips + ledger. */
export const LIVE_STATUS_COLOR_TOKEN = {
  scheduled: 'success',
  active: 'success',
  in_air: 'success',
  landed: 'success',
  cancelled: 'error',
  delayed: 'warning',
  diverted: 'error',
  incident: 'error',
};

export const LIVE_STATUS_TEXT_COLORS = {
  success: '#2F6B3A',
  warning: '#A67C00',
  error: '#8C2B22',
  info: '#A67C00',
  primary: '#2F6B3A',
  muted: '#8A7F74',
};

const clientCache = new Map();
const CLIENT_CACHE_MS = 30 * 60 * 1000;

export const toFlightDateKey = (value) => {
  if (!value) return null;
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return null;
  return format(date, 'yyyy-MM-dd');
};

export const resolveFlightIataParts = ({
  airline = '',
  flightNumber = '',
  airlineCode = '',
} = {}) => {
  const fromNumber = parseFlightNumberInput(String(flightNumber || '').replace(/\s+/g, ''));
  if (fromNumber?.airline && fromNumber?.flight_number) {
    return {
      airline: fromNumber.airline,
      flight_number: fromNumber.flight_number,
      flight_iata: `${fromNumber.airline}${fromNumber.flight_number}`,
    };
  }

  const digits = String(flightNumber || '').replace(/\D/g, '');
  const iata = resolveAirlineIata(airline, airlineCode)
    || resolveAirlineIata(String(airline || '').split(/\s+/).slice(0, 2).join(' '));

  if (iata && digits) {
    return {
      airline: iata,
      flight_number: digits,
      flight_iata: `${iata}${digits}`,
    };
  }

  const combined = parseFlightNumberInput(`${airline || ''}${flightNumber || ''}`.replace(/\s+/g, ''));
  if (combined?.airline && combined?.flight_number) {
    return {
      airline: combined.airline,
      flight_number: combined.flight_number,
      flight_iata: `${combined.airline}${combined.flight_number}`,
    };
  }

  return null;
};

export const liveStatusCacheKey = ({ flightIata, departureDate }) =>
  `${flightIata}|${toFlightDateKey(departureDate) || 'any'}`;

export const getLiveStatusTextColor = (token) =>
  LIVE_STATUS_TEXT_COLORS[token] || LIVE_STATUS_TEXT_COLORS.muted;

/** Days before departure when AviationStack live lookups are worth calling. */
export const LIVE_STATUS_LOOKAHEAD_DAYS = 1;
/** Days after departure to keep polling (delays / landed confirmation). */
export const LIVE_STATUS_LOOKBEHIND_DAYS = 1;

/**
 * Live gate/terminal data is only useful near the flying day.
 * Far-future itineraries should use the booked schedule without burning API quota.
 */
export const isWithinLiveStatusWindow = (departureTime, { now = Date.now() } = {}) => {
  if (!departureTime) return false;
  const dep = new Date(departureTime).getTime();
  if (Number.isNaN(dep)) return false;
  const aheadMs = LIVE_STATUS_LOOKAHEAD_DAYS * 24 * 60 * 60 * 1000;
  const behindMs = LIVE_STATUS_LOOKBEHIND_DAYS * 24 * 60 * 60 * 1000;
  return now >= (dep - aheadMs) && now <= (dep + behindMs);
};

/**
 * When AviationStack has no row yet (future itinerary, free-plan limits),
 * derive a simple status from the booked times so the card is never blank.
 */
export const getScheduleFallbackStatus = (departureTime, arrivalTime = null) => {
  const now = Date.now();
  const dep = departureTime ? new Date(departureTime).getTime() : null;
  const arr = arrivalTime ? new Date(arrivalTime).getTime() : null;

  if (dep && !Number.isNaN(dep) && now < dep) {
    return {
      success: true,
      live: false,
      status: 'scheduled',
      color: LIVE_STATUS_COLOR_TOKEN.scheduled,
      labelKey: LIVE_STATUS_LABEL_KEYS.scheduled,
      source: 'schedule',
    };
  }
  if (dep && arr && !Number.isNaN(dep) && !Number.isNaN(arr) && now >= dep && now < arr) {
    return {
      success: true,
      live: false,
      status: 'in_air',
      color: LIVE_STATUS_COLOR_TOKEN.in_air,
      labelKey: LIVE_STATUS_LABEL_KEYS.in_air,
      source: 'schedule',
    };
  }
  if (arr && !Number.isNaN(arr) && now >= arr) {
    return {
      success: true,
      live: false,
      status: 'landed',
      color: LIVE_STATUS_COLOR_TOKEN.landed,
      labelKey: LIVE_STATUS_LABEL_KEYS.landed,
      source: 'schedule',
    };
  }
  return {
    success: true,
    live: false,
    status: 'scheduled',
    color: LIVE_STATUS_COLOR_TOKEN.scheduled,
    labelKey: LIVE_STATUS_LABEL_KEYS.scheduled,
    source: 'schedule',
  };
};

export const mapLiveStatusPayload = (payload, { departureTime, arrivalTime } = {}) => {
  if (payload?.success && payload.status) {
    const status = String(payload.status).toLowerCase();
    const isLive = payload.source === 'live' || payload.source === 'aviationstack';
    return {
      success: true,
      live: isLive,
      status,
      color: LIVE_STATUS_COLOR_TOKEN[status] || 'info',
      labelKey: LIVE_STATUS_LABEL_KEYS[status] || LIVE_STATUS_LABEL_KEYS.scheduled,
      departure: payload.departure || null,
      arrival: payload.arrival || null,
      airline_name: payload.airline_name || null,
      flight_number: payload.flight_number || null,
      last_updated: payload.last_updated || null,
      source: payload.source || (isLive ? 'live' : 'schedule'),
    };
  }

  const fallback = payload?.schedule || getScheduleFallbackStatus(departureTime, arrivalTime);
  return {
    ...fallback,
    available: payload?.available,
    error: payload?.error || 'lookup_failed',
  };
};

/**
 * Live operational status for one flight/date (AviationStack via backend).
 * Shared by itinerary leg badges and My Trips chips.
 */
export const fetchLiveFlightStatus = async ({
  airline,
  flightNumber,
  departureDate,
  arrivalTime,
  departureAirport,
  arrivalAirport,
  signal,
  force = false,
  preferSchedule = false,
} = {}) => {
  const parts = resolveFlightIataParts({ airline, flightNumber });
  if (!parts) {
    return { success: false, error: 'flight_number_required' };
  }

  const outsideWindow = !force && !isWithinLiveStatusWindow(departureDate);
  const useScheduleOnly = preferSchedule || outsideWindow;

  const dateKey = toFlightDateKey(departureDate);
  const key = `${liveStatusCacheKey({ flightIata: parts.flight_iata, departureDate: dateKey })}|${useScheduleOnly ? 'sched' : 'live'}|${departureAirport || ''}|${arrivalAirport || ''}`;
  const cached = clientCache.get(key);
  if (cached && Date.now() - cached.at < CLIENT_CACHE_MS) {
    return cached.payload;
  }

  // Far-future / schedule-only: use booked itinerary times — no Duffel or AviationStack.
  if (useScheduleOnly) {
    const schedule = getScheduleFallbackStatus(departureDate, arrivalTime);
    const payload = {
      success: true,
      available: true,
      source: 'itinerary',
      flight_number: parts.flight_iata,
      status: schedule.status,
      airline_name: null,
      departure: {
        airport: departureAirport || null,
        terminal: null,
        gate: null,
        delay: null,
        scheduled: departureDate || null,
        estimated: null,
        actual: null,
      },
      arrival: {
        airport: arrivalAirport || null,
        terminal: null,
        gate: null,
        delay: null,
        scheduled: arrivalTime || null,
        estimated: null,
        actual: null,
      },
      last_updated: new Date().toISOString(),
    };
    clientCache.set(key, { at: Date.now(), payload });
    return payload;
  }

  try {
    const response = await api.get('/transportation/live-status', {
      params: {
        flight_number: parts.flight_iata,
        ...(dateKey ? { departure_date: dateKey } : {}),
        ...(departureAirport ? { departure_airport: departureAirport } : {}),
        ...(arrivalAirport ? { arrival_airport: arrivalAirport } : {}),
      },
      signal,
    });
    const payload = response.data;
    clientCache.set(key, { at: Date.now(), payload });
    return payload;
  } catch (error) {
    if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
      throw error;
    }
    const payload = {
      success: false,
      available: error?.response?.data?.available,
      error: error?.response?.data?.error || error.message || 'lookup_failed',
      schedule: getScheduleFallbackStatus(departureDate, arrivalTime),
    };
    if (error?.response?.status === 404 || error?.response?.status === 503) {
      clientCache.set(key, { at: Date.now(), payload });
    }
    return payload;
  }
};

/**
 * Batch live lookups. Dedupes by flight+date and uses the same client cache.
 */
export const fetchLiveFlightStatusesBatch = async (requests = [], { signal } = {}) => {
  const unique = new Map();
  requests.forEach((request) => {
    const parts = resolveFlightIataParts(request);
    if (!parts) return;
    const dateKey = toFlightDateKey(request.departureDate || request.departure_date);
    const key = liveStatusCacheKey({ flightIata: parts.flight_iata, departureDate: dateKey });
    if (!unique.has(key)) {
      unique.set(key, {
        key,
        airline: parts.airline,
        flight_number: parts.flight_iata,
        departure_date: dateKey || undefined,
        departureDateTime: request.departureDate || request.departure_date || null,
        arrivalTime: request.arrivalTime || request.arrival_time || null,
        departureAirport: request.departureAirport || request.departure_airport || null,
        arrivalAirport: request.arrivalAirport || request.arrival_airport || null,
        preferSchedule: Boolean(request.preferSchedule || request.prefer_schedule),
      });
    }
  });

  const entries = Array.from(unique.values());
  if (!entries.length) return {};

  const uncached = [];
  const result = {};

  entries.forEach((entry) => {
    const outsideWindow = !isWithinLiveStatusWindow(entry.departureDateTime);
    const preferSchedule = entry.preferSchedule || outsideWindow;
    const cached = clientCache.get(entry.key);
    if (cached && Date.now() - cached.at < CLIENT_CACHE_MS) {
      result[entry.key] = mapLiveStatusPayload(cached.payload, {
        departureTime: entry.departureDateTime,
        arrivalTime: entry.arrivalTime,
      });
      return;
    }
    if (preferSchedule) {
      const schedule = getScheduleFallbackStatus(entry.departureDateTime, entry.arrivalTime);
      const payload = {
        success: true,
        available: true,
        source: 'itinerary',
        flight_number: entry.flight_number,
        status: schedule.status,
        departure: {
          airport: entry.departureAirport || null,
          scheduled: entry.departureDateTime || null,
        },
        arrival: {
          airport: entry.arrivalAirport || null,
          scheduled: entry.arrivalTime || null,
        },
      };
      clientCache.set(entry.key, { at: Date.now(), payload });
      result[entry.key] = mapLiveStatusPayload(payload, {
        departureTime: entry.departureDateTime,
        arrivalTime: entry.arrivalTime,
      });
      return;
    }
    uncached.push(entry);
  });

  if (uncached.length) {
    try {
      const response = await api.post(
        '/transportation/live-status/batch',
        {
          flights: uncached.map(({
            flight_number,
            departure_date,
            departureAirport,
            arrivalAirport,
          }) => ({
            flight_number,
            departure_date,
            departure_airport: departureAirport || undefined,
            arrival_airport: arrivalAirport || undefined,
          })),
        },
        { signal }
      );
      const batchResults = response.data?.results || [];
      uncached.forEach((entry, index) => {
        const payload = batchResults[index] || { success: false, error: 'lookup_failed' };
        clientCache.set(entry.key, { at: Date.now(), payload });
        result[entry.key] = mapLiveStatusPayload(payload, {
          departureTime: entry.departureDateTime,
          arrivalTime: entry.arrivalTime,
        });
      });
    } catch (error) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
        throw error;
      }
      await Promise.all(
        uncached.map(async (entry) => {
          const payload = await fetchLiveFlightStatus({
            airline: entry.airline,
            flightNumber: entry.flight_number,
            departureDate: entry.departureDateTime || entry.departure_date,
            arrivalTime: entry.arrivalTime,
            preferSchedule: Boolean(entry.preferSchedule),
            signal,
          });
          result[entry.key] = mapLiveStatusPayload(payload, {
            departureTime: entry.departureDateTime,
            arrivalTime: entry.arrivalTime,
          });
        })
      );
    }
  }

  return result;
};

/** @private test helper */
export const __clearLiveFlightStatusCache = () => clientCache.clear();
